<template>
  <v-app>
    <!-- app-bar and drawer are siblings of v-main, not children of it. Nesting
         them inside v-main put them outside Vuetify's layout calculation, which
         is why the bar previously needed a hand-written `position: fixed`. -->
    <template v-if="isAuthed">
      <v-app-bar :elevation="1" class="brand-bar" height="64">
        <v-app-bar-nav-icon
          v-if="isCompact"
          variant="text"
          @click.stop="drawer = !drawer"
        ></v-app-bar-nav-icon>

        <router-link to="/" class="brand-mark">
          <img :src="logo" alt="Sea Tow" class="brand-logo" />
          <span v-if="showTitleText" class="brand-name">Call Agent Helper</span>
        </router-link>

        <v-spacer></v-spacer>

        <template v-slot:append>
          <!-- Inline nav only where it actually fits. The labels here are the
               short forms; the drawer carries the full names. The previous
               version showed six full-length labels from 900px up, which
               overflowed the bar and clipped "Sign Out" off the edge. -->
          <template v-if="!isCompact">
            <v-btn
              v-for="item in navItems"
              :key="item.to"
              :to="item.to"
              :title="item.title"
              class="nav-btn"
              size="small"
              variant="text"
            >{{ item.short }}</v-btn>
            <v-divider vertical class="mx-2 my-3"></v-divider>
          </template>

          <v-btn icon variant="text" size="small" :title="themeLabel" @click="toggleTheme">
            <v-icon>{{ themeIcon }}</v-icon>
          </v-btn>

          <v-btn
            v-if="!isCompact"
            size="small"
            variant="text"
            prepend-icon="mdi-logout"
            @click="onSignOut"
          >Sign Out</v-btn>
        </template>
      </v-app-bar>

      <v-navigation-drawer v-if="isCompact" v-model="drawer" temporary>
        <div class="pa-4">
          <img :src="logo" alt="Sea Tow" class="brand-logo" />
        </div>
        <v-divider></v-divider>
        <v-list nav density="comfortable">
          <v-list-item
            v-for="item in navItems"
            :key="item.to"
            :to="item.to"
            :title="item.title"
            :prepend-icon="item.icon"
          ></v-list-item>
          <v-divider class="my-2"></v-divider>
          <v-list-item :title="themeLabel" :prepend-icon="themeIcon" @click="toggleTheme"></v-list-item>
          <v-list-item title="Sign Out" prepend-icon="mdi-logout" @click="onSignOut"></v-list-item>
        </v-list>
      </v-navigation-drawer>
    </template>

    <v-main>
      <router-view></router-view>
    </v-main>

    <!-- Sign-out used to fail silently into console.error. -->
    <v-snackbar v-model="signOutFailed" color="error" timeout="6000">
      Could not sign out. Please try again.
    </v-snackbar>
  </v-app>
</template>

<script>
import { account, signOut } from "./services/auth";
import logoOnWhite from "./assets/ST_logo_onwhite.png";
import logoOnBlack from "./assets/ST_logo_onblack.png";

export default {
  name: "App",
  data: () => ({
    drawer: false,
    signOutFailed: false,
    // `short` is what the app bar can afford to render; `title` is the real
    // name, used in the drawer and as the button's tooltip.
    navItems: [
      { title: "Home", short: "Home", icon: "mdi-home-outline", to: "/" },
      {
        title: "Change Login Email",
        short: "Login Email",
        icon: "mdi-email-edit-outline",
        to: "/change-email",
      },
      {
        title: "Change Membership #",
        short: "Membership #",
        icon: "mdi-card-account-details-outline",
        to: "/change-membership",
      },
      {
        title: "Update Virtual Membership Card",
        short: "Virtual Card",
        icon: "mdi-wallet-membership",
        to: "/update-card",
      },
      {
        title: "Resend Login Code",
        short: "Login Code",
        icon: "mdi-lock-reset",
        to: "/resend-code",
      },
    ],
  }),
  computed: {
    isAuthed() {
      return !!account.value;
    },
    isCompact() {
      return this.$vuetify.display.smAndDown;
    },
    // Drop the wordmark in the band where the inline nav is showing but space
    // is tight, so the nav never has to compete with it.
    showTitleText() {
      return this.isCompact || this.$vuetify.display.lgAndUp;
    },
    isDark() {
      return this.$vuetify.theme.global.current.dark;
    },
    logo() {
      return this.isDark ? logoOnBlack : logoOnWhite;
    },
    themeIcon() {
      return this.isDark ? "mdi-weather-sunny" : "mdi-weather-night";
    },
    themeLabel() {
      return this.isDark ? "Light mode" : "Dark mode";
    },
  },
  methods: {
    toggleTheme() {
      const name = this.isDark ? "light" : "dark";
      this.$vuetify.theme.global.name = name;
      localStorage.setItem("theme", name);
    },
    async onSignOut() {
      try {
        await signOut();
        this.$router.push("/login");
      } catch (error) {
        console.error(error);
        this.signOutFailed = true;
      }
    },
  },
  watch: {
    // close the mobile drawer whenever the route changes
    $route() {
      this.drawer = false;
    },
  },
};
</script>

<style scoped>
/* Brand yellow as a rule under the bar — the one place the accent can carry
   weight without failing contrast. */
.brand-bar {
  border-bottom: 3px solid #ffe716;
}

.brand-mark {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  text-decoration: none;
  color: inherit;
  margin-left: 8px;
}

.brand-logo {
  height: 22px;
  width: auto;
}

.brand-name {
  font-weight: 600;
  letter-spacing: 0.01em;
  white-space: nowrap;
}

.nav-btn {
  letter-spacing: 0.02em;
}

/* Active route gets the accent, so the current tool is obvious at a glance. */
.nav-btn.v-btn--active {
  border-bottom: 2px solid #ffe716;
  border-radius: 0;
}
</style>
